import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  errorMessage, toastError, toastSuccess,
} from '../../../lib/toast';
import { adminCreateSpeciality, adminListTags, adminToggleSpeciality, listOf } from '../../vendors/vendorsApi';
import { VENDOR_ERRORS } from '../../vendors/vendorLabels';
import { Dialog, FullError, Skeleton } from '../../vendors/VendorBits';
import SpecialityDrawer from './SpecialityDrawer';
import '../../vendors/Vendors.css';

const NAME_MAX = 80;

/**
 * Every synced Odoo tag with its vendor count and an "is speciality" switch.
 * Only specialities carry access, so turning one off asks first.
 */
export default function VendorSpecialitiesPanel() {
  const [state, setState] = useState({ status: 'loading', tags: [] });
  const [filter, setFilter] = useState('');
  const [confirmOff, setConfirmOff] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [open, setOpen] = useState(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, status: 'loading' }));
    adminListTags()
      .then((d) => setState({ status: 'ready', tags: listOf(d, 'tags') }))
      .catch(() => setState({ status: 'error', tags: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setSpeciality = async (tag, on) => {
    setBusyId(tag.id);
    try {
      await adminToggleSpeciality(tag.id, on);
      setState((s) => ({ ...s, tags: s.tags.map((t) => (t.id === tag.id ? { ...t, is_speciality: on } : t)) }));
    } catch (err) {
      toastError(errorMessage(err, 'Could not update the speciality.', VENDOR_ERRORS));
    } finally {
      setBusyId(null);
    }
  };

  const onToggle = (tag) => {
    if (tag.is_speciality) setConfirmOff(tag);
    else setSpeciality(tag, true);
  };

  const create = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const res = await adminCreateSpeciality(name);
      toastSuccess(res?.created === false ? 'Speciality already existed in Odoo — now enabled.' : 'Speciality created.');
      setNewName('');
      load();
    } catch (err) {
      toastError(errorMessage(err, 'Could not create the speciality.', VENDOR_ERRORS));
    } finally {
      setCreating(false);
    }
  };

  // Specialities first (they are what admins come back to manage), then the tags
  // most vendors carry. Order is stable while a switch is flipped, so a row the
  // admin just turned on doesn't jump away under the cursor.
  const order = useMemo(
    () => new Map([...state.tags]
      .sort((a, b) => (Number(!!b.is_speciality) - Number(!!a.is_speciality))
        || ((b.vendor_count ?? 0) - (a.vendor_count ?? 0))
        || a.name.localeCompare(b.name))
      .map((t, i) => [t.id, i])),
    // Recomputed on load only, not on each toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.status],
  );
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q ? state.tags.filter((t) => t.name.toLowerCase().includes(q)) : state.tags;
    return [...list].sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  }, [state.tags, filter, order]);

  let body;
  if (state.status === 'loading' && !state.tags.length) body = <Skeleton label="Loading specialities" />;
  else if (state.status === 'error') body = <FullError message="Couldn't load specialities. Try again in a minute." onRetry={load} />;
  else if (!state.tags.length) body = <div className="vd-empty"><p>No Odoo tags have synced yet. Run the vendor sync to pull them in.</p></div>;
  else if (!shown.length) body = <div className="vd-empty"><p>No tags match “{filter.trim()}”.</p></div>;
  else {
    body = (
      <div className="vd-table-wrap">
        <table className="vd-table">
          <thead>
            <tr><th scope="col">Tag</th><th scope="col" className="vd-num">Vendors</th><th scope="col">Speciality</th></tr>
          </thead>
          <tbody>
            {shown.map((t) => (
              <tr key={t.id}>
                <td data-label="Tag">
                  {t.is_speciality
                    ? (
                      <button type="button" className="vd-link-btn" onClick={() => setOpen(t)}>
                        {t.name}
                        <span className="vd-link-hint" aria-hidden="true">Manage access</span>
                      </button>
                    )
                    : <span dir="auto">{t.name}</span>}
                </td>
                <td data-label="Vendors" className={`vd-num ${t.vendor_count ? '' : 'vd-muted'}`}>{t.vendor_count ?? 0}</td>
                <td data-label="Speciality">
                  <input
                    type="checkbox"
                    role="switch"
                    className="vd-switch"
                    aria-label={`${t.name} is a speciality`}
                    checked={!!t.is_speciality}
                    disabled={busyId === t.id}
                    onChange={() => onToggle(t)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const onCount = state.tags.filter((t) => t.is_speciality).length;

  return (
    <section className="vd-panel vd-spec-panel" aria-labelledby="vd-adm-spec">
      <div className="vd-panel-head">
        <h2 id="vd-adm-spec" className="vd-panel-title">Specialities</h2>
        {state.status === 'ready' && state.tags.length > 0 && (
          <span className="vd-panel-meta">{`${onCount} of ${state.tags.length} tags on`}</span>
        )}
      </div>
      <p className="vd-panel-lede">
        Turn on the Odoo tags that group vendors by what they supply. Only specialities carry access: select one to choose who can see its vendors.
      </p>
      <div className="vd-toolbar">
        <input type="search" className="search-input" aria-label="Filter specialities" placeholder="Filter tags by name" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <form className="vd-inline-form" onSubmit={create}>
          <label htmlFor="vd-new-spec" className="sr-only">New speciality</label>
          <input id="vd-new-spec" type="text" className="search-input" placeholder="New speciality name" maxLength={NAME_MAX} value={newName} onChange={(e) => setNewName(e.target.value)} disabled={creating} />
          <button type="submit" className="btn btn-primary vd-btn-compact" disabled={creating || !newName.trim()} aria-busy={creating || undefined}>Add speciality</button>
        </form>
      </div>
      {body}

      {confirmOff && (
        <Dialog title={`Turn off ${confirmOff.name}?`} onClose={() => setConfirmOff(null)} labelledBy="vd-spec-off-title">
          <p>People who get access only through this speciality will lose it straight away. The tag stays in Odoo.</p>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmOff(null)}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={() => { const t = confirmOff; setConfirmOff(null); setSpeciality(t, false); }}>Turn off speciality</button>
          </div>
        </Dialog>
      )}
      {open && <SpecialityDrawer tag={open} onClose={() => setOpen(null)} />}
    </section>
  );
}
